import {
  RefObject,
  useEffect,
  useRef,
} from 'react';
import {
  GridApiPro,
  GridColumnVisibilityModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { useLocation } from 'react-router';
import {
  isUserScopedSavedFilterType,
  SavedFilterGridState,
  SavedFilterType,
} from 'shared';

import { useFetchSavedFiltersQuery } from '../services/api/savedFiltersApi/savedFilters';

import { useGetSelectedOrganisation } from './useGetSelectedOrganisation';

interface UseApplySavedFilterFromUrlOptions {
  // Defers applying while the grid isn't mounted (e.g. the invoice kanban view)
  enabled?: boolean;
  // The table's default column spec. Applied whenever a saved filter doesn't
  // define columns, and re-applied when the active saved filter is cleared
  // (e.g. a quick filter or the plain table link was clicked), so column
  // visibility never leaks from a previous saved filter.
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
  // Called with the saved state just before it is restored, for table state
  // outside the grid (gridState.extra)
  onApply?: (gridState: SavedFilterGridState) => void;
}

// A ?savedFilterId=<id> param (e.g. from the sidebar shortcuts or the filter
// panel) applies the saved MUI grid state to the table. restoreState feeds
// filter/sort back through the grid's normal model-change handlers, so server
// mode & Redux stay in sync. Returns the param so table roots can skip their
// first-mount filter reset.
export function useApplySavedFilterFromUrl(
  apiRef: RefObject<GridApiPro>,
  type: SavedFilterType,
  { enabled = true, defaultColumnVisibilityModel, onApply }: UseApplySavedFilterFromUrlOptions = {},
) {
  const location = useLocation();
  const currentOrg = useGetSelectedOrganisation();

  const savedFilterId = new URLSearchParams(location.search).get(`savedFilterId`);

  // User-scoped types (reports) have no org context
  const orgScoped = !isUserScopedSavedFilterType(type);

  const { data } = useFetchSavedFiltersQuery({
    organisationId: orgScoped ? currentOrg?.id : undefined,
    type,
  }, {
    skip: orgScoped && !currentOrg?.id,
  });

  const appliedKey = useRef<string | null>(null);
  const appliedId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    if (!savedFilterId) {
      // The active saved filter was cleared: put the columns back to the
      // table's default spec. Also forget the applied key so navigating back
      // to the same history entry re-applies the saved filter.
      if (appliedId.current && defaultColumnVisibilityModel && apiRef.current?.setColumnVisibilityModel) {
        apiRef.current.setColumnVisibilityModel({ ...defaultColumnVisibilityModel });
        appliedId.current = null;
      }

      appliedKey.current = null;

      return;
    }

    // Keyed on the navigation so re-clicking a shortcut re-applies, but a
    // background refetch of the list doesn't stomp manual filter tweaks.
    const applyKey = `${location.key}:${savedFilterId}`;
    if (appliedKey.current === applyKey) return;

    const savedFilter = data?.savedFilters.find(f => f.id === savedFilterId);
    if (!savedFilter || !apiRef.current?.restoreState) return;

    // Clone: the cached response is frozen and downstream reducers mutate models
    const stateToRestore = JSON.parse(JSON.stringify(savedFilter.gridState)) as GridInitialState;

    // Saved filters without a column visibility spec reset the columns to
    // default; an existing visibility model is left untouched
    if (!stateToRestore.columns?.columnVisibilityModel && defaultColumnVisibilityModel) {
      stateToRestore.columns = {
        ...stateToRestore.columns,
        columnVisibilityModel: { ...defaultColumnVisibilityModel },
      };
    }

    onApply?.(savedFilter.gridState);
    apiRef.current.restoreState(stateToRestore);
    appliedKey.current = applyKey;
    appliedId.current = savedFilterId;
  }, [enabled, savedFilterId, data, location.key, apiRef, defaultColumnVisibilityModel, onApply]);

  return savedFilterId;
}
